import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

const AuthWrapper = ({ children }) => {
    const isAuthenticated = useIsAuthenticated();
    const { instance, inProgress } = useMsal();
    const location = useLocation();
    const navigate = useNavigate();
    const hasRedirected = useRef(false);
    const hasStoredPath = useRef(false);

    // Make MSAL instance available globally for userService
    useEffect(() => {
        window.msalInstance = instance;
        return () => {
            delete window.msalInstance;
        };
    }, [instance]);

    // Store intended destination immediately on mount if not authenticated
    useEffect(() => {
        const currentPath = location.pathname + location.search;

        // Store the path if user is not authenticated and it's not the login page
        if (!isAuthenticated && 
            currentPath !== '/login' && 
            currentPath !== '/' && 
            !hasStoredPath.current) {
            sessionStorage.setItem('intendedDestination', currentPath);
            hasStoredPath.current = true;
        }
    }, [location.pathname, location.search, isAuthenticated, inProgress]);

    // Handle redirect after authentication
    useEffect(() => {
        if (isAuthenticated && inProgress === InteractionStatus.None && !hasRedirected.current) {
            const intendedDestination = sessionStorage.getItem('intendedDestination');
            const currentPath = location.pathname + location.search;

            if (intendedDestination && intendedDestination !== currentPath) {
                hasRedirected.current = true;
                sessionStorage.removeItem('intendedDestination');
                
                // Immediate navigation
                navigate(intendedDestination, { replace: true });
                
                // Reset flag after delay
                setTimeout(() => {
                    hasRedirected.current = false;
                    hasStoredPath.current = false;
                }, 1000);
            } else if (intendedDestination) {
                // Clean up stored destination if we don't need it
                sessionStorage.removeItem('intendedDestination');
            }
        }
    }, [isAuthenticated, inProgress, navigate, location.pathname, location.search]);

    // Reset flags when authentication state changes
    useEffect(() => {
        if (!isAuthenticated) {
            hasRedirected.current = false;
            hasStoredPath.current = false;
        }
    }, [isAuthenticated]);

    return children;
};

export default AuthWrapper; 